<template>
    <ui5-dialog
        ref="dialog"
        :header-text="trans('pages.product_type_groups.group')"
        @after-close="$emit('close')"
        :stretch="true"
    >
        <pre
            v-if="isDebug"
            class="debug-fom"
            >{{ form }}</pre
        >
        <div class="form-container">
            <ui5-label
                v-if="languages"
                for="nameInput"
                required
            >
                {{ trans('pages.product_type_groups.name') }}
            </ui5-label>
            <ui5-input
                v-if="languages"
                id="nameInput"
                :value="form.name.value"
                @input="form.name.value = $event.target.value"
                :placeholder="
                    trans('pages.product_type_groups.enter_group_name')
                "
                :required="true"
                :show-suggestions="false"
                :disabled="saving"
            ></ui5-input>

            <translations-edit
                ref="translations-label"
                :translation-data="form.name.translations"
            ></translations-edit>

            <ui5-label>
                {{ trans('pages.product_type_groups.image') }}
            </ui5-label>
            <div class="image-uploader">
                <div
                    v-if="form.imagePathS3"
                    class="gallery-item"
                >
                    <div class="inner">
                        <i
                            class="fa fa-remove"
                            :class="{ disabled: saving }"
                            @click="!saving && removeImage()"
                        />
                        <img :src="form.imagePathS3" />
                    </div>
                </div>
                <div
                    class="drop-area"
                    ref="dropArea"
                    v-if="!form.imagePathS3"
                >
                    <ui5-file-uploader
                        hide-input
                        @change="handleFiles"
                        :accept="mimes.join(',')"
                        :disabled="saving"
                    >
                        <div class="uploader-content">
                            <span>
                                {{
                                    trans('pages.product_type_groups.add_image')
                                }}
                            </span>
                            <br />
                            <i class="fa fa-upload" />
                            <i class="fa fa-camera" />
                        </div>
                    </ui5-file-uploader>
                </div>
            </div>
        </div>

        <div
            slot="footer"
            class="dialog-footer"
        >
            <ui5-button
                @click="save"
                :disabled="saving"
            >
                <spinner v-if="saving" />
                {{ trans('pages.product_type_groups.save') }}
            </ui5-button>
            <ui5-button
                @click="close"
                :disabled="saving"
            >
                {{ trans('pages.product_type_groups.cancel') }}
            </ui5-button>
        </div>
    </ui5-dialog>
</template>

<script>
import IsDebugMixin from '../../mixins/is-debug';
import localization from '../../mixins/localization';
import Spinner from '../loader/Spinner';
import TranslationsEdit from '../TranslationsEdit';

export default {
    name: 'ProductTypeGroupEditForm',
    mixins: [localization, IsDebugMixin],
    components: { Spinner, TranslationsEdit },
    props: {
        group: {
            type: [Object, null],
            default: null,
        },
        saving: {
            type: Boolean,
            default: false,
        },
        languages: {
            type: Array,
        },
    },
    data() {
        return {
            form: {
                name: {
                    value: '',
                    translations: {},
                },
                imagePathS3: '',
            },
            mimes: ['image/png', 'image/jpeg'],
        };
    },
    watch: {
        group: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.form.name.value = newVal.name || '';
                    this.form.imagePathS3 = newVal.imagePathS3 || '';
                    this.form.name.translations = Object.fromEntries(
                        Object.entries(newVal.translations || {}).map(
                            ([lang, value]) => [lang, value.trim()]
                        )
                    );
                } else {
                    this.form.name.value = '';
                    this.form.imagePathS3 = '';
                    this.form.name.translations = {};
                }
            },
        },
    },
    mounted() {
        ['dragenter', 'dragover', 'dragleave', 'drop'].forEach((eventName) => {
            this.$refs.dropArea.addEventListener(
                eventName,
                this.preventDefaults,
                false
            );
        });

        ['dragenter', 'dragover'].forEach((eventName) => {
            this.$refs.dropArea.addEventListener(
                eventName,
                this.highlight,
                false
            );
        });

        ['dragleave', 'drop'].forEach((eventName) => {
            this.$refs.dropArea.addEventListener(
                eventName,
                this.unhighlight,
                false
            );
        });

        this.$refs.dropArea.addEventListener('drop', this.handleDrop, false);
    },
    methods: {
        show() {
            this.$refs.dialog.show();
        },
        close() {
            this.$refs.dialog.close();
        },
        save() {
            if (this.saving) {
                return; // Prevent multiple saves
            }
            const payload = {
                id: this.group ? this.group.id : null,
                parentId: this.group ? this.group.parentId : null,
                name: {
                    value: this.form.name.value.trim(),
                    translations: Object.fromEntries(
                        Object.entries(this.form.name.translations || {}).map(
                            ([lang, value]) => [lang, value.trim()]
                        )
                    ),
                },
                imagePathS3: this.form.imagePathS3 || null,
            };
            this.$emit('save', payload);
        },
        preventDefaults(e) {
            e.preventDefault();
            e.stopPropagation();
        },
        highlight() {
            if (!this.saving) {
                this.$refs.dropArea.classList.add('drop-area-highlight');
            }
        },
        unhighlight() {
            this.$refs.dropArea.classList.remove('drop-area-highlight');
        },
        handleFiles($event) {
            if (this.saving) return;
            const files = [...$event.target.files];
            this.handleFilesInner(files);
        },
        handleDrop(e) {
            if (this.saving) return;
            const files = [...e.dataTransfer.files];
            this.handleFilesInner(files);
        },
        handleFilesInner(files) {
            const file = files[0]; // Single file
            if (file && this.mimes.includes(file.type)) {
                this.createFileWithPreview(file).then((fileItem) => {
                    this.form.imagePathS3 = fileItem.previewSrc;
                });
            } else {
                console.warn('Invalid file type. Allowed:', this.mimes);
            }
        },
        createFileWithPreview(file) {
            return new Promise((resolve) => {
                const result = {
                    fileObject: file,
                    newFile: true,
                    previewSrc: '',
                };
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onloadend = () => {
                    result.previewSrc = reader.result;
                    resolve(result);
                };
            });
        },
        removeImage() {
            this.form.imagePathS3 = '';
        },
    },
};
</script>

<style
    lang="scss"
    scoped
>
.debug-fom {
    max-width: 100%;
    overflow: scroll;
}
.form-container {
    padding: 1em;
    display: flex;
    flex-direction: column;
    gap: 0.5em;
    min-width: 300px;

    ui5-label {
        font-weight: bold;
        margin-bottom: 0.2em;
    }

    ui5-input {
        width: 100%;
    }
}

.dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.5em;
    padding: 0.5em;
}

.image-uploader {
    display: flex;
    flex-direction: column;
    gap: 0.5em;
}

.gallery-item {
    position: relative;
    width: 100px;
    height: 100px;

    .inner {
        position: relative;
        width: 100%;
        height: 100%;

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }

        .fa-remove {
            position: absolute;
            top: 5px;
            right: 5px;
            cursor: pointer;
            color: red;
            font-size: 1em;

            &.disabled {
                cursor: not-allowed;
                opacity: 0.5;
            }
        }
    }
}

.drop-area {
    border: 2px dashed #ccc;
    padding: 1em;
    text-align: center;
    transition: border-color 0.3s;

    &.drop-area-highlight {
        border-color: #333;
    }
}

.uploader-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5em;
    color: #666;

    .fa-upload,
    .fa-camera {
        font-size: 1.5em;
    }
}
</style>
